import React, { useEffect, useState } from 'react';
import {
    Box,
    Checkbox,
    Button,
    Typography,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    CircularProgress,
    TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import '../../../styles/bankTokens.css';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import {
    CARD_AUTO_MATCH_REFERENCE_OPTIONS,
    DEFAULT_REFERENCE_LAST_DIGITS,
    hasPartialReferenceSelection,
} from '../../../Utils/matchingUtils';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    backgroundColor: 'var(--bank-surface-muted, #f7f8fc)',
    border: '1px solid var(--bank-border, #e4e7f2)',
    borderRadius: '30px',
    padding: '3px',
    flexShrink: 0,
    '& .MuiToggleButton-root': {
        backgroundColor: 'transparent',
        border: 'none',
        color: theme.palette.text.primary,
        transition: 'background-color 0.2s',
        borderRadius: '30px',
        padding: '4px 12px',
        minWidth: '96px',
        height: '30px',
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'var(--Foundation-Blue-Light, #EAEDFF)',
        },
        '&.Mui-selected': {
            backgroundColor: 'var(--Foundation-Blue-Normal, #304FFF)',
            color: '#fff',
            '&:hover': {
                backgroundColor: '#1E40FF',
            },
        },
    },
}));

const actionButtonSx = {
    minWidth: 100,
    height: 32,
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    px: 1.25,
};

// Sits in the layout flow at the full width of the workspace, like the bank screen:
// full width keeps the centre segment roomy enough for the approve button, and being
// in flow means it can never cover the last rows of the two lists.
const dockBarSx = {
    width: '100%',
    boxSizing: 'border-box',
    px: 1.25,
    py: 0.75,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    backgroundColor: 'var(--bank-surface, #fff)',
    border: '1px solid var(--bank-border, #e4e7f2)',
    borderRadius: 'var(--bank-radius, 12px)',
    boxShadow: 'var(--bank-shadow, 0 2px 8px rgba(0, 0, 0, 0.08))',
};

const centreGroupSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    flex: '1 1 auto',
    minWidth: 0,
    flexWrap: 'wrap',
};

const summaryPanelSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 1 200px',
    minWidth: 120,
    px: 1,
    py: 0.5,
    borderRadius: 'var(--bank-radius-sm, 8px)',
    backgroundColor: 'var(--bank-surface-muted, #f7f8fc)',
};

const summaryLabelSx = {
    fontSize: '0.65rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'var(--bank-text-muted, #6b7186)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
};

const summaryValueSx = {
    fontSize: '0.95rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: 'var(--bank-text, #1f2437)',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
};

const CardMatchingBottomDockBar = ({
    debitColumns,
    handleJournalEntries,
    handleMatchEntries,
    handleAutoMatch,
    handleMatch,
    handleClearMatches,
    debitSum,
    creditSum,
    matches,
    selectedDebitRows,
    selectedCreditRows,
    showMatchOptions = true,
    loading = false,
    direction,
}) => {
    const { t, i18n } = useTranslation();
    const dir = direction || i18n.dir();
    const [selectedColumns, setSelectedColumns] = useState(['amount']);
    const [referenceLastDigits, setReferenceLastDigits] = useState(DEFAULT_REFERENCE_LAST_DIGITS);
    const [createJournalEntries, setCreateJournalEntries] = useState(false);
    const [matchType, setMatchType] = useState('Manual'); // Toggle between "Manual" and "Automatic"

    const handleCreateJournalEntries = () => {
        handleJournalEntries(createJournalEntries);
    };

    // Handle multi-select for columns
    const handleColumnSelect = (event) => {
        setSelectedColumns(event.target.value);
    };

    // Handle toggle between matching types
    const handleMatchTypeChange = (event, newMatchType) => {
        if (newMatchType !== null && !loading) {
            setMatchType(newMatchType);
        }
    };

    // Handle Apply click in Automatic Matching
    const handleAutomaticApply = () => {
        if (selectedColumns.length === 0 || !selectedColumns.includes('amount')) {
            alert('Please select at least the Amount column for matching.');
            return;
        }
        if (hasPartialReferenceSelection(selectedColumns) && (!referenceLastDigits || referenceLastDigits < 1)) {
            alert(t('ReferenceLastDigitsRequired'));
            return;
        }
        handleAutoMatch(selectedColumns, referenceLastDigits);
    };

    // Handle Clear action
    const handleClear = () => {
        handleClearMatches();
        setSelectedColumns(['amount']);
        setReferenceLastDigits(DEFAULT_REFERENCE_LAST_DIGITS);
    };

    const difference = (debitSum || 0) - (creditSum || 0);

    // Helper function to check if difference is effectively zero (within tolerance)
    const isDifferenceZero = (diff) => Math.abs(diff) < 0.01;

    const selectedDebitCount = selectedDebitRows.length;
    const selectedCreditCount = selectedCreditRows.length;
    const hasSelection = selectedDebitCount > 0 || selectedCreditCount > 0;

    // The two sides agree and something is actually selected, so the match can
    // be approved (same condition F5 uses).
    const canReconcile = showMatchOptions && isDifferenceZero(difference) && hasSelection;

    // Anything left to offer once approving lives in the summary panel.
    const hasManualActions = showMatchOptions && !canReconcile && hasSelection;

    // Only one side selected: the other side does not exist yet, so the leftover
    // can be booked as journal entries.
    const oneSidedSelection =
        (selectedDebitCount > 0 && selectedCreditCount === 0) ||
        (selectedCreditCount > 0 && selectedDebitCount === 0);

    // Debit total on the right, credit total on the left, difference and the
    // reconcile action in the middle. DOM order is always debit → centre → credit;
    // the flex direction puts the debit segment on the right in both reading
    // directions, the same way the two lists are laid out above.
    const summaryRowSx = {
        display: 'flex',
        flexDirection: dir === 'rtl' ? 'row' : 'row-reverse',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 1,
    };

    useEffect(() => {
        if (!(oneSidedSelection && !isDifferenceZero(difference))) {
            setCreateJournalEntries(false);
        }
    }, [oneSidedSelection, difference]);

    return (
        <Box sx={dockBarSx} dir={dir}>
            <Box sx={summaryRowSx}>
                <Box sx={summaryPanelSx}>
                    <Typography sx={summaryLabelSx}>
                        {selectedDebitCount > 0 ? `${t('Debit')} (${selectedDebitCount})` : t('Debit')}
                    </Typography>
                    <Typography sx={summaryValueSx}>{beautifyNumber(debitSum || 0)}</Typography>
                </Box>

                <Box sx={centreGroupSx}>
                    <Box
                        sx={{
                            ...summaryPanelSx,
                            flexDirection: 'row',
                            flex: '0 0 auto',
                            gap: 1,
                            px: 1,
                            backgroundColor: canReconcile
                                ? 'var(--Foundation-Blue-Light, #EAEDFF)'
                                : 'var(--bank-surface-muted, #f7f8fc)',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                            <Typography sx={summaryLabelSx}>{t('Difference')}</Typography>
                            <Typography
                                sx={{
                                    ...summaryValueSx,
                                    color: isDifferenceZero(difference)
                                        ? 'var(--bank-text, #1f2437)'
                                        : 'var(--bank-negative, #d32f2f)',
                                }}
                            >
                                {beautifyNumber(difference)}
                            </Typography>
                        </Box>
                        {showMatchOptions && (
                            <Tooltip title={t('ApproveReconciliationHint')} arrow>
                                <span>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleMatch(selectedDebitRows, selectedCreditRows)}
                                        disabled={loading || !canReconcile}
                                        sx={{
                                            ...actionButtonSx,
                                            minWidth: 0,
                                            height: 30,
                                            backgroundColor: 'var(--Foundation-Blue-Normal, #304FFF)',
                                            color: '#fff',
                                            '&:hover': { backgroundColor: '#1E40FF' },
                                        }}
                                        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                                    >
                                        {`${t('ApproveReconciliation')} · F5`}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>

                    {showMatchOptions && (
                        <StyledToggleButtonGroup
                            color="primary"
                            value={matchType}
                            exclusive
                            onChange={handleMatchTypeChange}
                            aria-label="Matching Type"
                            disabled={loading}
                        >
                            <ToggleButton value="Manual">{t('ManualMatching')}</ToggleButton>
                            <ToggleButton value="Automatic">{t('AutomaticMatching')}</ToggleButton>
                        </StyledToggleButtonGroup>
                    )}

                    {/* Manual matching actions: the balanced case is served by the
                        approve button above, so what is left is the unbalanced path. */}
                    {matchType === 'Manual' && hasManualActions && (
                        <>
                            {oneSidedSelection && !isDifferenceZero(difference) && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        checked={createJournalEntries}
                                        onChange={(e) => setCreateJournalEntries(e.target.checked)}
                                        color="primary"
                                        disabled={loading}
                                        size="small"
                                    />
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {t('CreateJournalEntriesForEachTransaction')}
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                onClick={handleCreateJournalEntries}
                                disabled={loading}
                                sx={{
                                    ...actionButtonSx,
                                    backgroundColor: 'var(--orange, #F9AA2A)',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#E6951F' },
                                }}
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {loading ? t('Loading') || '...' : t('CreateJournalEntry')}
                            </Button>
                        </>
                    )}

                    {/* Automatic matching controls */}
                    {showMatchOptions && matchType === 'Automatic' && (matches.length <= 0 ? (
                        <>
                            <FormControl size="small" sx={{ minWidth: 160, maxWidth: 200 }}>
                                <InputLabel>{t('SelectHeaders')}</InputLabel>
                                <Select
                                    multiple
                                    value={selectedColumns}
                                    onChange={handleColumnSelect}
                                    label={t('SelectHeaders')}
                                    disabled={loading}
                                    renderValue={(selected) => selected.map((item) => t(item)).join(', ')}
                                    sx={{ backgroundColor: 'var(--bank-surface-muted, #f7f8fc)', height: 34 }}
                                >
                                    {debitColumns.map((col) => (
                                        <MenuItem key={col.field} value={col.field}>
                                            {t(col.headerName)}
                                        </MenuItem>
                                    ))}
                                    {CARD_AUTO_MATCH_REFERENCE_OPTIONS.map((col) => (
                                        <MenuItem key={col.field} value={col.field}>
                                            {t(col.headerName)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {hasPartialReferenceSelection(selectedColumns) && (
                                <TextField
                                    type="number"
                                    label={t('ReferenceLastDigits')}
                                    value={referenceLastDigits}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (!isNaN(value)) {
                                            setReferenceLastDigits(Math.min(20, Math.max(1, value)));
                                        }
                                    }}
                                    inputProps={{ min: 1, max: 20 }}
                                    size="small"
                                    disabled={loading}
                                    sx={{ width: 110, flexShrink: 0 }}
                                />
                            )}

                            <Button
                                variant="contained"
                                onClick={handleAutomaticApply}
                                disabled={loading}
                                sx={{
                                    ...actionButtonSx,
                                    backgroundColor: 'var(--Foundation-Blue-Normal, #304FFF)',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#1E40FF' },
                                }}
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {loading ? t('Loading') || '...' : t('Apply')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                onClick={handleMatchEntries}
                                disabled={loading}
                                sx={{
                                    ...actionButtonSx,
                                    backgroundColor: 'var(--bank-positive, #1B8A5A)',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#15734A' },
                                }}
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {loading ? t('Loading') || '...' : t('Match')}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleClear}
                                disabled={loading}
                                sx={actionButtonSx}
                            >
                                {t('Clear')}
                            </Button>
                        </>
                    ))}
                </Box>

                <Box sx={summaryPanelSx}>
                    <Typography sx={summaryLabelSx}>
                        {selectedCreditCount > 0 ? `${t('Credit')} (${selectedCreditCount})` : t('Credit')}
                    </Typography>
                    <Typography sx={summaryValueSx}>{beautifyNumber(creditSum || 0)}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default CardMatchingBottomDockBar;
